import { normalizeItemNotas } from '../utils/formatters';
import {
    calcularFechaVencimDesdeVtoComprob,
    resolveFechaVencimForSave,
    toDateOnly,
    toFechaVencimISO,
    validarFechasFacturaVenta,
} from '../utils/facturaVentaFechas';
import {
    resolveFechaVencimFromApi,
    resolveVtoComprobComprobanteFromApi,
} from '../utils/facturaVentaVencim';
import api from './api';
import clienteService from './clienteService';
import productoService from './productoService';
import transporteService from './transporteService';
import movMonedaService from './movMonedaService';
import {
    pedidoTieneLineasConPrecioCero,
    precioListaDetallePedido,
    prepararDetallesParaCreateConPrecioCero,
} from '../utils/pedidoVentaLineas';
import { buildVentaSearchQueryParams, VentaListSearchParamsBase } from '../utils/ventaSearchQueryParams';
import { estadoDesdeRegistroPedido, normalizeEstadoPedidoMaster } from '../utils/pedidoVentaEstados';
import { umDesdeDetalleTransaccion } from '../utils/umVentaDocumento';
import { normalizeUmSnapshotPedFromApi } from '../utils/umDocumentoDisplay';
import {
    mapCopiarPedidoRelacionadosResult,
    mapTenantRelacionadoPedido,
} from './pedidoVentaCopiarRelacionados';
import type {
    CopiarPedidoRelacionadosResult,
    TenantRelacionadoPedido,
} from './pedidoVentaCopiarRelacionados';
const DEFAULT_NRO_SUC = 1;

export type ConversionAutomaticaModo = 'maestros' | 'porLinea';

export type ConversionAutomaticaOptions = {
  modo?: ConversionAutomaticaModo;
};

export type {
    TenantRelacionadoPedido,
    CopiarPedidoRelacionadoDestino,
    CopiarPedidoRelacionadosResult,
} from './pedidoVentaCopiarRelacionados';

export interface PedidoVentaDetalle {
    nroIdformulario: number;
    nroItem: number;
    nroFormularioMaster?: number;
    nroItemMaster?: number;
    codigoArticulo?: string;
    descripcionArticulo?: string;
    cantidad?: number;
    /** Unidades anotadas como pedido en espera. No descuentan el importe del pedido. */
    cantidadesPES?: number | null;
    /** Unidades canceladas del renglón. No descuentan el importe del pedido. */
    cantidadCanc?: number | null;
    /** Cantidad convertida / a facturar (conversión automática). */
    cantidadB?: number | null;
    precioUnitario?: number;
    precioLista?: number;
    /** Precio unitario mínimo neto del artículo (cliente; no se envía al API). */
    precioMinimo?: number | null;
    descuento?: number;
    porcDesc?: number;
    subtotal?: number;
    /** Subtotal de la porción convertida (CantidadB). */
    subtotalCantB?: number | null;
    /** Porcentaje de conversión aplicado en el renglón. */
    porcSubtotalCantB?: number | null;
    /** % de conversión del producto/renglón (antes o al aplicar conv. automática). */
    porcCantB?: number | null;
    /** Precio unitario con IVA (consumidor final). */
    precioUnitarioFinal?: number;
    /** Subtotal con IVA (consumidor final). */
    subtotalFinal?: number;
    notas?: string;
    um?: string; // Unidad de medida base
    /** Snapshot Ped: cantidad ingresada por el usuario. */
    cantidadUmped?: number;
    /** Snapshot Ped: UM ingresada. */
    umped?: string;
    /** Snapshot Ped: factor hacia UM base. */
    equivUmped?: number;
    /** Snapshot Ped: precio unitario ingresado (en umped). */
    precioUnitarioUmped?: number;
    codigoMoneda?: number; // Código de moneda
    cotizacion?: number; // Cotización
}

export interface PedidoVenta {
    nroFormulario: number;
    nroTipoFormulario: string;
    tipoCliente: string;
    fechaEmision: Date;
    fechaVencim?: string | Date;
    vtoComprob?: number | null;
    horaEmision: Date;
    nroCliente: number;
    nombre: string;
    direccionCliente?: string | null;
    direccionEnvio?: string | null;
    nroVendedor: number;
    nroLista: string;
    codigoMoneda: string;
    importeTotal: number;
    importeSinIva?: number;
    ivainscripto?: number;
    asentado: string;
    descripcion: string;
    usuario: string;
    detalles: PedidoVentaDetalle[];
    porcDesc?: number | null;
    porcDesc2?: number | null;
    porcDesc3?: number | null;
    tipoDesc?: string | null;
    tipoDesc2?: string | null;
    tipoDesc3?: string | null;
    importeDesc?: number | null;
    importeDesc2?: number | null;
    importeDesc3?: number | null;
    facturado?: string;
    nroFormularioFactura?: number;
    integrar?: number;
    estado?: string; // Estado del pedido: 'Nuevo', 'Preparado', 'Impreso', 'Entregado', 'Facturado', 'Anulado', 'Listo para pickear', etc.
    tipoIVA?: string;
    tipoIva?: string;
}

export interface PedidoVentaSearchParams extends VentaListSearchParamsBase {
    PendienteFacturar?: boolean;
    facturado?: string;
    nroFormularioFactura?: number;
}

export interface PedidoVentaPagedResult {
    items: PedidoVenta[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface PedidosVentaKpis {
    cantidadPedidos: number;
    precioTotal: number;
    precioNeto: number;
    pesoTotalKg: number;
    volumenTotalM3: number;
}

export type PedidosVentaKpisParams = Omit<PedidoVentaSearchParams, 'pageNumber' | 'pageSize'>;

export const emptyPedidosVentaKpis = (): PedidosVentaKpis => ({
    cantidadPedidos: 0,
    precioTotal: 0,
    precioNeto: 0,
    pesoTotalKg: 0,
    volumenTotalM3: 0,
});

const resolveKpiNumber = (data: unknown, camel: string, pascal: string): number => {
    const payload = data as Record<string, unknown> | null | undefined;
    const raw = payload?.[camel] ?? payload?.[pascal];
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : 0;
};

const normalizePedidosVentaKpis = (data: unknown): PedidosVentaKpis => ({
    cantidadPedidos:
        resolveKpiNumber(data, 'cantidadPedidos', 'CantidadPedidos') ||
        resolveKpiNumber(data, 'cantidad', 'Cantidad'),
    precioTotal: resolveKpiNumber(data, 'precioTotal', 'PrecioTotal'),
    precioNeto: resolveKpiNumber(data, 'precioNeto', 'PrecioNeto'),
    pesoTotalKg: resolveKpiNumber(data, 'pesoTotalKg', 'PesoTotalKg'),
    volumenTotalM3: resolveKpiNumber(data, 'volumenTotalM3', 'VolumenTotalM3'),
});

const withPedidoNotasTrimmed = (data: PedidoVenta): PedidoVenta => ({
    ...data,
    detalles: (data.detalles || []).map((d) => {
        const raw = d as PedidoVentaDetalle & {
            Notas?: string | null;
            Cantidad?: number | null;
            CantidadesPES?: number | null;
            cantidadPes?: number | null;
            CantidadPes?: number | null;
            CantidadCanc?: number | null;
            CantidadB?: number | null;
            SubtotalCantB?: number | null;
            PorcSubtotalCantB?: number | null;
            porc_SubtotalCantB?: number | null;
            PorcCantB?: number | null;
            Porc_CantB?: number | null;
        };
        return {
            ...d,
            notas: normalizeItemNotas(raw.notas ?? raw.Notas),
            um: umDesdeDetalleTransaccion(raw) || undefined,
            ...normalizeUmSnapshotPedFromApi(raw),
            cantidad: Number(raw.cantidad ?? raw.Cantidad ?? d.cantidad ?? 0),
            cantidadesPES: Number(
                raw.cantidadesPES ?? raw.CantidadesPES ?? raw.cantidadPes ?? raw.CantidadPes ?? 0,
            ) || 0,
            cantidadCanc: Number(raw.cantidadCanc ?? raw.CantidadCanc ?? 0) || 0,
            cantidadB: raw.cantidadB ?? raw.CantidadB ?? null,
            subtotalCantB: raw.subtotalCantB ?? raw.SubtotalCantB ?? null,
            porcSubtotalCantB:
                raw.porcSubtotalCantB ?? raw.PorcSubtotalCantB ?? raw.porc_SubtotalCantB ?? null,
            porcCantB: raw.porcCantB ?? raw.PorcCantB ?? raw.Porc_CantB ?? null,
        };
    }),
});

const normalizePedidoVentaFromApi = (raw: any): PedidoVenta => {
    const fechaVencimIso = resolveFechaVencimFromApi(raw);
    const vtoComprob = resolveVtoComprobComprobanteFromApi(raw);
    const base = withPedidoNotasTrimmed({
        ...(raw as PedidoVenta),
        detalles: raw?.detalles ?? raw?.Detalles ?? [],
    });
    const estadoRaw = estadoDesdeRegistroPedido(raw as Record<string, unknown>);
    return {
        ...base,
        ...(fechaVencimIso != null ? { fechaVencim: fechaVencimIso } : {}),
        ...(vtoComprob != null ? { vtoComprob } : {}),
        ...(estadoRaw ? { estado: normalizeEstadoPedidoMaster(estadoRaw) } : {}),
    };
};

const round5 = (n: number) => Math.round(n * 100000) / 100000;

const toPrecioItem = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const resolverPrecioListaCreateItem = (
    detalle: PedidoVentaDetalle,
    productoData?: any,
    cotizacionUSD?: number | null,
): number => {
    if (detalle.precioUnitario != null && Number.isFinite(Number(detalle.precioUnitario))) {
        return precioListaDetallePedido(detalle);
    }

    const precioProducto = productoData?.precioVenta ?? 0;
    if (precioProducto && precioProducto > 0) {
        const tipoMonedaProd = productoData?.tipoMoneda ?? productoData?.tipoMonedaArticulo ?? 0;
        if (Number(tipoMonedaProd) === 1 && cotizacionUSD && cotizacionUSD > 0) {
            return Number((precioProducto * cotizacionUSD).toFixed(2));
        }
        return Number(precioProducto);
    }
    return 0;
};

const extraerNroFormularioPedido = (data: any): number | null => {
    const candidatos = [
        data?.nroFormulario,
        data?.formulario?.nroFormulario,
        data?.data?.nroFormulario,
    ];
    for (const value of candidatos) {
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
    return null;
};

const resolveMasterNumber = (detalle: any, keys: string[]): number | undefined => {
    for (const key of keys) {
        const value = detalle?.[key];
        if (value === undefined || value === null || value === '') {
            continue;
        }
        const parsed = Number(value);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return undefined;
};

const pedidoVentaService = {
    getPedidosVenta: async (params: PedidoVentaSearchParams): Promise<PedidoVentaPagedResult> => {
        const queryParams = buildVentaSearchQueryParams(params);
        if (params.facturado) {
            queryParams.Facturado = params.facturado;
        }
        if (params.nroFormularioFactura != null && params.nroFormularioFactura > 0) {
            queryParams.NroFormularioFactura = params.nroFormularioFactura;
        }
        if (params.PendienteFacturar) {
            queryParams.PendienteFacturar = true;
        }
        const response = await api.get<PedidoVentaPagedResult>('/api/PedidosVenta', { params: queryParams });
        return {
            ...response.data,
            items: (response.data.items || []).map((item) => normalizePedidoVentaFromApi(item)),
        };
    },

    /**
     * Totales de KPI del filtro completo (sin paginación).
     * GET /api/PedidosVenta/kpis — mismos filtros que el listado, sin PageNumber/PageSize.
     */
    getPedidosVentaKpis: async (params: PedidosVentaKpisParams): Promise<PedidosVentaKpis> => {
        const queryParams = buildVentaSearchQueryParams({
            pageNumber: 1,
            pageSize: 1,
            searchTerm: params.searchTerm,
            fechaDesde: params.fechaDesde,
            fechaHasta: params.fechaHasta,
            nroCliente: params.nroCliente,
            nroVendedor: params.nroVendedor,
            nroSuc: params.nroSuc,
            asentado: params.asentado,
            estado: params.estado,
            usuario: params.usuario,
        });

        if (params.facturado) {
            queryParams.Facturado = params.facturado;
        }
        if (params.nroFormularioFactura != null && params.nroFormularioFactura > 0) {
            queryParams.NroFormularioFactura = params.nroFormularioFactura;
        }
        if (params.PendienteFacturar) {
            queryParams.PendienteFacturar = true;
        }

        const response = await api.get('/api/PedidosVenta/kpis', { params: queryParams });
        return normalizePedidosVentaKpis(response.data);
    },

    getPedidoVenta: async (id: number, opts?: { sinCache?: boolean }): Promise<PedidoVenta> => {
        const response = await api.get<PedidoVenta>(`/api/pedidosventa/${id}`, {
            params: opts?.sinCache ? { _: Date.now() } : undefined,
        });
        return normalizePedidoVentaFromApi(response.data);
    },

    createPedidoVenta: async (pedidoVenta: PedidoVenta & { 
        nroSuc?: number; 
        ordenCompra?: string; 
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
        idCompania?: string;
    }, options?: { enEspera?: boolean }): Promise<PedidoVenta> => {
        // Validaciones de datos obligatorios
        const errors: string[] = [];

        // Validar cliente
        if (!pedidoVenta.nroCliente || pedidoVenta.nroCliente <= 0) {
            errors.push('nroCliente es obligatorio y debe ser mayor a 0');
        }

        // Obtener datos del cliente antes de crear el payload
        let clienteData: any = null;
        if (pedidoVenta.nroCliente && pedidoVenta.nroCliente > 0) {
            try {
                clienteData = await clienteService.getCliente(pedidoVenta.nroCliente);
                if (!clienteData) {
                    errors.push(`No se encontró el cliente con número ${pedidoVenta.nroCliente}`);
                }
            } catch (error: any) {
                const mensajeError = error?.response?.status === 404 
                    ? `No se encontró el cliente con número ${pedidoVenta.nroCliente}`
                    : `Error al obtener los datos del cliente: ${error?.message || 'Error desconocido'}`;
                errors.push(mensajeError);
            }
        }

        // Validar que haya al menos un detalle
        if (!pedidoVenta.detalles || pedidoVenta.detalles.length === 0) {
            errors.push('Debe haber al menos un item en el pedido');
        }

        // Validar cada detalle y obtener datos de productos
        const productosData: Record<string, any> = {};
        if (pedidoVenta.detalles && pedidoVenta.detalles.length > 0) {
            // Obtener datos completos de cada producto
            for (const detalle of pedidoVenta.detalles) {
                if (detalle.codigoArticulo) {
                    try {
                        const producto = await productoService.getProducto(detalle.codigoArticulo);
                        if (!producto) {
                            errors.push(`No se encontró el producto con código "${detalle.codigoArticulo}"`);
                        } else {
                            productosData[detalle.codigoArticulo] = producto;
                        }
                    } catch (error: any) {
                        const itemIndex = pedidoVenta.detalles.findIndex(d => d.codigoArticulo === detalle.codigoArticulo) + 1;
                        const mensajeError = error?.response?.status === 404
                            ? `Item ${itemIndex}: No se encontró el producto con código "${detalle.codigoArticulo}"`
                            : `Item ${itemIndex}: Error al obtener el producto "${detalle.codigoArticulo}": ${error?.message || 'Error desconocido'}`;
                        errors.push(mensajeError);
                    }
                }
            }

            pedidoVenta.detalles.forEach((detalle, index) => {
                const itemNum = index + 1;
                // Validar codigoArticulo
                if (!detalle.codigoArticulo || String(detalle.codigoArticulo).trim() === '') {
                    errors.push(`Item ${itemNum}: codigoArticulo es obligatorio`);
                }
                // Validar descripcionArticulo
                if (!detalle.descripcionArticulo || String(detalle.descripcionArticulo).trim() === '') {
                    errors.push(`Item ${itemNum}: descripcion es obligatoria`);
                }
                // Validar cantidad
                if (detalle.cantidad === undefined || detalle.cantidad === null) {
                    errors.push(`Item ${itemNum}: cantidad es obligatoria`);
                } else if (typeof detalle.cantidad !== 'number' || isNaN(detalle.cantidad)) {
                    errors.push(`Item ${itemNum}: cantidad debe ser un número válido (tipo: ${typeof detalle.cantidad}, valor: ${detalle.cantidad})`);
                } else if (detalle.cantidad <= 0) {
                    errors.push(`Item ${itemNum}: cantidad debe ser mayor a 0 (valor actual: ${detalle.cantidad})`);
                }
            });
        }

        // Validar fechas
        let fechaEmisionDate = new Date();
        if (!pedidoVenta.fechaEmision) {
            errors.push('La Fecha de Emisión es obligatoria. Por favor, ingrese la fecha en la sección "Información del Pedido"');
        } else {
            fechaEmisionDate = pedidoVenta.fechaEmision instanceof Date
                ? pedidoVenta.fechaEmision
                : new Date(pedidoVenta.fechaEmision);
            if (isNaN(fechaEmisionDate.getTime())) {
                errors.push('La Fecha de Emisión debe ser una fecha válida');
            }
        }

        const fechaVencimDate = resolveFechaVencimForSave(fechaEmisionDate, pedidoVenta.fechaVencim);
        const fechaVencimError = validarFechasFacturaVenta(
            fechaEmisionDate,
            fechaVencimDate,
            calcularFechaVencimDesdeVtoComprob(fechaEmisionDate, pedidoVenta.vtoComprob),
        );
        if (fechaVencimError) {
            errors.push(fechaVencimError);
        }

        // Validar tipos de datos numéricos
        if (pedidoVenta.nroCliente !== undefined && (typeof pedidoVenta.nroCliente !== 'number' || isNaN(pedidoVenta.nroCliente))) {
            errors.push('nroCliente debe ser un número válido');
        }

        if ((pedidoVenta as any).nroSuc !== undefined && (pedidoVenta as any).nroSuc !== null) {
            if (typeof (pedidoVenta as any).nroSuc !== 'number' || isNaN((pedidoVenta as any).nroSuc)) {
                errors.push('nroSuc debe ser un número válido');
            }
        }

        // Si hay errores, lanzarlos con mensaje claro
        if (errors.length > 0) {
            const errorMessage = errors.length === 1 
                ? errors[0]
                : `Se encontraron ${errors.length} errores:\n${errors.map((e, i) => `${i + 1}. ${e}`).join('\n')}`;
            console.error('❌ Errores de validación:', errors);
            throw new Error(errorMessage);
        }

        // Obtener la cotización USD solo si falta precio en el detalle y hay que usar el catálogo
        let cotizacionUSD: number | null = null;
        const requiereFallbackCatalogo = pedidoVenta.detalles.some(
            (d) => d.precioUnitario == null || !Number.isFinite(Number(d.precioUnitario)),
        );
        if (requiereFallbackCatalogo) {
            try {
                const cotizacion = await movMonedaService.getUltimaCotizacion();
                if (cotizacion && Number.isFinite(cotizacion) && cotizacion > 0) {
                    cotizacionUSD = cotizacion;
                }
            } catch (error) {
                console.warn('⚠️ No se pudo obtener cotización USD para fallback:', error);
            }
        }

        // Mapear los detalles a la estructura de items esperada por el endpoint
        // IMPORTANTE: El precio del detalle ya viene convertido a ARS (con cotización aplicada si era USD)
        // El precio 0 explícito se respeta; solo se usa catálogo si el detalle no trae precio
        const requiereActualizacionPorPrecioCero = pedidoTieneLineasConPrecioCero(pedidoVenta.detalles);
        const detallesParaCreate = requiereActualizacionPorPrecioCero
            ? prepararDetallesParaCreateConPrecioCero(pedidoVenta.detalles ?? [])
            : (pedidoVenta.detalles ?? []);

        const items = detallesParaCreate.map((detalle) => {
            const productoData = productosData[detalle.codigoArticulo || ''];
            const precioLista = resolverPrecioListaCreateItem(detalle, productoData, cotizacionUSD);
            const porcDesc = detalle.descuento ?? detalle.porcDesc ?? productoData?.porc_Desc ?? productoData?.porcDesc ?? 0;

            // Como el precio ya está convertido a ARS, siempre se envía codigoMoneda: 0 y cotizacion: 0
            const codigoMoneda = 0;
            const cotizacion = 0;

            const nroFormularioMaster = resolveMasterNumber(detalle as any, [
                'nroFormularioMaster',
                'nroformulariomaster',
                'NroFormularioMaster',
                'nro_formulario_master',
            ]);
            const nroItemMaster = resolveMasterNumber(detalle as any, [
                'nroItemMaster',
                'nroitemmaster',
                'NroItemMaster',
                'nro_item_master',
            ]);

            return {
                codigoArticulo: String(detalle.codigoArticulo ?? '').trim(),
                descripcion: String(detalle.descripcionArticulo ?? productoData?.descripcion ?? '').trim(),
                cantidad: Number(detalle.cantidad ?? 0.01),
                cantidadesPES: Number(detalle.cantidadesPES ?? 0) || 0,
                cantidadCanc: Number(detalle.cantidadCanc ?? 0) || 0,
                precioUnitario: toPrecioItem(precioLista),
                porcDesc: toPrecioItem(porcDesc),
                nroFormularioMaster,
                nroItemMaster,
                um: detalle.um ?? '',
                codigoMoneda: Number(codigoMoneda),
                cotizacion: Number(cotizacion),
                notas: normalizeItemNotas(detalle.notas),
                ...(detalle.porcSubtotalCantB != null && Number(detalle.porcSubtotalCantB) > 0
                  ? { porcSubtotalCantB: Number(detalle.porcSubtotalCantB) }
                  : {}),
                ...(detalle.porcCantB != null && Number(detalle.porcCantB) > 0
                  ? { porcCantB: Number(detalle.porcCantB) }
                  : {}),
            };
        });

        // Extraer nroUbicacion del campo ubicación (formato: "NOMBRE (número)")
        // El endpoint espera nroUbicacionOrigen como número (int), no el nombre
        let nroUbicacionOrigen = 0;
        if ((pedidoVenta as any).ubicacion) {
            try {
                const ubicacionStr = String((pedidoVenta as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroUbicacionOrigen = Number(match[1]);
                }
            } catch (error) {
                // Si hay error, dejar en 0
                nroUbicacionOrigen = 0;
            }
        }

        // Obtener el valor de formaEntrega del transporte seleccionado
        // El valor debe ser el campo formaEntrega del Transporte obtenido mediante GET api/Transportes/{nroExpreso}
        // Por ejemplo: si se selecciona PROPIO (nroExpreso=1), se hace GET api/Transportes/1
        // y el valor de formaEntrega será el valor del campo formaEntrega del objeto Transporte (puede ser null)
        let formaEntrega: string | null = null;
        const transporteSeleccionado = String((pedidoVenta as any).transporte ?? '').trim();
        if (transporteSeleccionado) {
            try {
                // Obtener el transporte específico mediante GET api/Transportes/{nroExpreso}
                const transporteEncontrado = await transporteService.getTransporte(transporteSeleccionado);
                
                if (transporteEncontrado) {
                    // Usar el campo formaEntrega del transporte obtenido
                    // Puede ser null, string, o undefined
                    const formaEntregaRaw = transporteEncontrado.formaEntrega ?? (transporteEncontrado as any).formaEntrega;
                    
                    if (formaEntregaRaw !== null && formaEntregaRaw !== undefined) {
                        // Si tiene valor, convertirlo a string
                        formaEntrega = String(formaEntregaRaw).trim() || null;
                    } else {
                        // Si es null o undefined, dejar null
                        formaEntrega = null;
                    }
                    
                    console.log('🔍 Transporte obtenido mediante GET api/Transportes/{nroExpreso}:', transporteSeleccionado);
                    console.log('🔍 formaEntrega del transporte:', formaEntregaRaw);
                    console.log('📤 formaEntrega a enviar:', formaEntrega);
                } else {
                    console.warn('⚠️ No se encontró el transporte con nroExpreso:', transporteSeleccionado);
                }
            } catch (error) {
                console.error('❌ Error al obtener transporte mediante GET api/Transportes/{nroExpreso}:', error);
                // Si hay error, dejar null
                formaEntrega = null;
            }
        }

        const fechaEmisionISO = toFechaVencimISO(fechaEmisionDate);
        const fechaVencimISO = toFechaVencimISO(fechaVencimDate);

        const getPorcentajeBonificacion = (...values: Array<number | string | null | undefined>) => {
            for (const value of values) {
                if (value === null || value === undefined) continue;
                const num = Number(value);
                if (Number.isFinite(num)) {
                    return Math.max(0, Math.min(100, num));
                }
            }
            return 0;
        };

        const porcDescValue = getPorcentajeBonificacion(
            (pedidoVenta as any).porcDesc,
            clienteData?.porcDesc,
            (clienteData as any)?.porc_Desc
        );
        const porcDesc2Value = getPorcentajeBonificacion(
            (pedidoVenta as any).porcDesc2,
            clienteData?.porcDesc2,
            (clienteData as any)?.porc_Desc2
        );
        const porcDesc3Value = getPorcentajeBonificacion(
            (pedidoVenta as any).porcDesc3,
            clienteData?.porcDesc3,
            (clienteData as any)?.porc_Desc3
        );

        // Construir el objeto formulario según la estructura del endpoint
        const nroSucValueRaw = (pedidoVenta as any).nroSuc;
        const nroSucValue = Number.isFinite(Number(nroSucValueRaw)) && Number(nroSucValueRaw) > 0
            ? Number(nroSucValueRaw)
            : DEFAULT_NRO_SUC;

        const tipoIvaValue = (pedidoVenta as any).tipoIVA ?? (pedidoVenta as any).tipoIva ?? clienteData?.tipoIva ?? '';

        const nroVendedorPedido = Number(pedidoVenta.nroVendedor ?? 0);

        const formulario: any = {
            descripcion: String(pedidoVenta.descripcion ?? '').trim(), // Del campo Descripción
            nroSuc: nroSucValue, // Desde el formulario (fallback: 1)
            fechaEmision: fechaEmisionISO, // Del campo Fecha de Emisión
            fechaVencim: fechaVencimISO,
            nroCliente: Number(clienteData?.nroCliente ?? pedidoVenta.nroCliente ?? 0), // Del cliente obtenido
            ...(Number.isFinite(nroVendedorPedido) && nroVendedorPedido > 0 ? { nroVendedor: nroVendedorPedido } : {}),
            nroLista: String(clienteData?.nroLista ?? pedidoVenta.nroLista ?? '').trim(), // Del cliente obtenido
            formaDePago: String((pedidoVenta as any).formaDePago ?? '').trim(), // De la sección Forma de Pago
            nroUbicacionOrigen: Number(nroUbicacionOrigen), // nroUbicacion extraído de la ubicación seleccionada (número)
            ordenDeCompra: String((pedidoVenta as any).ordenCompra ?? '').trim(), // Del campo Orden de Compra
            codigoDireccion: '', // Vacío por ahora
            codigoDireccionEnvio: '', // Vacío por ahora
            DireccionEnvio: String(pedidoVenta.direccionEnvio ?? '').trim(),
            formaEntrega: formaEntrega ?? '', // Valor del campo formaEntrega del transporte seleccionado (puede ser null, se envía como string vacío)
            porcDesc: porcDescValue,
            porcDesc2: porcDesc2Value,
            porcDesc3: porcDesc3Value,
            tipoDesc: String((pedidoVenta as any).tipoDesc ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P',
            tipoDesc2: String((pedidoVenta as any).tipoDesc2 ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P',
            tipoDesc3: String((pedidoVenta as any).tipoDesc3 ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P',
            importeDesc: Number((pedidoVenta as any).importeDesc ?? 0) || 0,
            importeDesc2: Number((pedidoVenta as any).importeDesc2 ?? 0) || 0,
            importeDesc3: Number((pedidoVenta as any).importeDesc3 ?? 0) || 0,
        };

        if (tipoIvaValue && String(tipoIvaValue).trim() !== '') {
            formulario.tipoIva = String(tipoIvaValue).trim();
        }

        const nombrePedido = String(pedidoVenta.nombre ?? '').trim();
        if (nombrePedido) {
            formulario.nombre = nombrePedido;
        }
        
        // Determinar el valor a enviar en cuit
        // Si es consumidor final, enviar dni; en caso contrario, enviar cuit
        const esConsumidorFinal = tipoIvaValue === 'C' || tipoIvaValue === 'CF';
        const valorCuit = esConsumidorFinal 
            ? ((pedidoVenta as any).dni || (clienteData as any)?.dni || '')
            : ((pedidoVenta as any).cuit || clienteData?.cuit || '');
        
        // Agregar cuit al formulario solo si tiene un valor válido
        if (valorCuit && String(valorCuit).trim() !== '') {
            formulario.cuit = String(valorCuit).trim();
        }
        
        // Solo incluir codigoCiudadEnvio si tiene un valor válido (no 0 y no vacío)
        // Si no tiene valor, no incluir el campo en el payload
        const codigoCiudadEnvioValue = (pedidoVenta as any).codigoCiudadEnvio;
        if (codigoCiudadEnvioValue !== undefined && codigoCiudadEnvioValue !== null && codigoCiudadEnvioValue !== 0) {
            formulario.codigoCiudadEnvio = Number(codigoCiudadEnvioValue);
        }

        // Solo incluir idCompania si tiene un valor válido
        const idCompaniaValue = (pedidoVenta as any).idCompania;
        if (idCompaniaValue !== undefined && idCompaniaValue !== null && String(idCompaniaValue).trim() !== '') {
            formulario.idCompania = String(idCompaniaValue).trim();
        }

        // Incluir nroFormularioCotizacion si el pedido proviene de Preparar Pedido (para marcar cotización como facturada)
        const nroFormularioCotizacionValue = (pedidoVenta as any).nroFormularioCotizacion;
        if (nroFormularioCotizacionValue !== undefined && nroFormularioCotizacionValue !== null && Number(nroFormularioCotizacionValue) > 0) {
            formulario.nroFormularioCotizacion = Number(nroFormularioCotizacionValue);
        }

        // Preparar el payload según la especificación del endpoint
        const payload = {
            formulario,
            items
        };

        const endpoint = options?.enEspera ? '/api/PedidosVenta/pes' : '/api/PedidosVenta';
        
        // Log detallado para depuración
        console.log('📤 ===== INICIO PETICIÓN POST =====');
        console.log('📤 Endpoint:', endpoint);
        console.log('📤 Base URL:', api.defaults.baseURL);
        console.log('📤 Método: POST');
        console.log('📤 Payload completo:', JSON.stringify(payload, null, 2));
        console.log('📤 URL completa será:', `${api.defaults.baseURL}${endpoint}`);
        console.log('📤 ===== FIN LOG PRE-ENVÍO =====');

        try {
            // El interceptor de axios ya agrega automáticamente el token y Content-Type
            const response = await api.post<PedidoVenta>(endpoint, payload);
            
            // Log de respuesta exitosa
            console.log(`✅ ===== RESPUESTA EXITOSA =====`);
            console.log('✅ Endpoint usado:', endpoint);
            console.log('✅ Status:', response.status);
            console.log('✅ Data:', response.data);
            console.log('✅ ===== FIN RESPUESTA =====');

            if (requiereActualizacionPorPrecioCero) {
                const nroFormulario = extraerNroFormularioPedido(response.data);
                if (!nroFormulario) {
                    throw new Error('El pedido se creó pero no se pudo obtener su número para guardar líneas con precio cero.');
                }
                await pedidoVentaService.updatePedidoVenta(nroFormulario, pedidoVenta);
                return pedidoVentaService.getPedidoVenta(nroFormulario);
            }
            
            return withPedidoNotasTrimmed(response.data);
        } catch (error: any) {
            console.error('❌ ===== ERROR EN PETICIÓN POST =====');
            console.error('❌ Endpoint:', endpoint);
            console.error('❌ Status:', error?.response?.status);
            console.error('❌ Status Text:', error?.response?.statusText);
            console.error('❌ URL que se intentó:', error?.config?.url);
            console.error('❌ URL completa:', `${error?.config?.baseURL || api.defaults.baseURL}${error?.config?.url}`);
            console.error('❌ Response Data:', error?.response?.data);
            console.error('❌ Request Data enviada:', error?.config?.data);
            console.error('❌ Headers enviados:', error?.config?.headers);
            console.error('❌ ===== FIN ERROR =====');
            
            throw error;
        }
    },

    updatePedidoVenta: async (id: number, pedidoVenta: PedidoVenta & { 
        nroSuc?: number; 
        ordenCompra?: string; 
        transporte?: string;
        ubicacion?: string;
        formaDePago?: string;
        idCompania?: string;
    }, options?: {
        /**
         * Conserva precioLista/precioUnitario/descuento del detalle tal como vinieron
         * (sin recalcular descuento %). Evita fallos de precio mínimo por redondeo
         * al solo cambiar cantidades / Cant.B / %.
         */
        preservarPrecios?: boolean;
    }): Promise<PedidoVenta> => {
        // El PUT debe usar la misma estructura que devuelve el GET (campos planos con detalles)
        // NO usar la estructura de creación (formulario + items)
        const preservarPrecios = options?.preservarPrecios === true;
        
        // Preparar fechas (misma normalización que en POST)
        const fechaEmisionDate = toDateOnly(pedidoVenta.fechaEmision) ?? new Date();
        const fechaEmisionISO = toFechaVencimISO(fechaEmisionDate);

        const fechaVencimDate = resolveFechaVencimForSave(fechaEmisionDate, pedidoVenta.fechaVencim);
        const fechaVencimError = validarFechasFacturaVenta(
            fechaEmisionDate,
            fechaVencimDate,
            calcularFechaVencimDesdeVtoComprob(fechaEmisionDate, (pedidoVenta as PedidoVenta).vtoComprob),
        );
        if (fechaVencimError) {
            throw new Error(fechaVencimError);
        }
        const fechaVencimISO = toFechaVencimISO(fechaVencimDate);

        // Extraer nroUbicacionOrigen de ubicación (formato: "NOMBRE (número)")
        let nroUbicacionOrigen: number | undefined = undefined;
        if ((pedidoVenta as any).ubicacion) {
            try {
                const ubicacionStr = String((pedidoVenta as any).ubicacion);
                const match = ubicacionStr.match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroUbicacionOrigen = Number(match[1]);
                }
            } catch (error) {
                console.warn('Error al extraer nroUbicacionOrigen:', error);
            }
        }

        // Preparar detalles asegurando que tengan nroIdformulario igual al id
        // NO incluir subtotal ya que el backend lo calcula
        // El campo descuento debe ser el MONTO DESCONTADO UNITARIO (por unidad), no el porcentaje
        // El precioUnitario debe ser el precio de lista MENOS el descuento unitario
        // Los montos unitarios deben redondearse a 5 decimales, no a 2
        const detalles = (pedidoVenta.detalles || []).map((detalle, index) => {
            const nroFormularioMaster = resolveMasterNumber(detalle as any, [
                'nroFormularioMaster',
                'nroformulariomaster',
                'NroFormularioMaster',
                'nro_formulario_master',
            ]);
            const nroItemMaster = resolveMasterNumber(detalle as any, [
                'nroItemMaster',
                'nroitemmaster',
                'NroItemMaster',
                'nro_item_master',
            ]);

            let precioLista: number;
            let precioUnitario: number;
            let descuentoMontoUnitario: number;

            if (preservarPrecios) {
                // Mantener precios del GET: no recalcular % ni redondear (evita "bajo el mínimo")
                const listaRaw = detalle.precioLista;
                const unitRaw = detalle.precioUnitario;
                precioLista =
                    listaRaw != null && Number.isFinite(Number(listaRaw))
                        ? Number(listaRaw)
                        : precioListaDetallePedido(detalle);
                precioUnitario =
                    unitRaw != null && Number.isFinite(Number(unitRaw))
                        ? Number(unitRaw)
                        : precioLista;
                descuentoMontoUnitario = Math.max(0, precioLista - precioUnitario);
            } else {
                precioLista = precioListaDetallePedido(detalle);
                const descuentoPorcentaje = Number(detalle.descuento ?? detalle.porcDesc ?? 0);
                descuentoMontoUnitario = precioLista * (descuentoPorcentaje / 100);
                precioUnitario = precioLista - descuentoMontoUnitario;
                precioLista = round5(precioLista);
                precioUnitario = round5(precioUnitario);
                descuentoMontoUnitario = round5(descuentoMontoUnitario);
            }
            const detallePayload: any = {
                nroIdformulario: id,
                nroItem: detalle.nroItem || (index + 1),
                nroFormularioMaster,
                nroItemMaster,
                codigoArticulo: detalle.codigoArticulo || '',
                descripcionArticulo: detalle.descripcionArticulo || '',
                cantidad: detalle.cantidad || 0,
                cantidadesPES: Number(detalle.cantidadesPES ?? 0) || 0,
                cantidadCanc: Number(detalle.cantidadCanc ?? 0) || 0,
                precioLista,
                precioUnitario,
                descuento: descuentoMontoUnitario,
                notas: normalizeItemNotas(detalle.notas),
            };
            
            // Incluir UM siempre (también vacía) para no perderla en updates
            detallePayload.um = String(detalle.um ?? '').trim();
            if (detalle.codigoMoneda !== undefined && detalle.codigoMoneda !== null) {
                detallePayload.codigoMoneda = detalle.codigoMoneda;
            }
            if (detalle.cotizacion !== undefined && detalle.cotizacion !== null) {
                detallePayload.cotizacion = detalle.cotizacion;
            }
            if (detalle.porcDesc !== undefined && detalle.porcDesc !== null) {
                detallePayload.porcDesc = detalle.porcDesc;
            }
            if (detalle.cantidadB !== undefined && detalle.cantidadB !== null) {
                detallePayload.cantidadB = Number(detalle.cantidadB);
            }
            if (detalle.subtotalCantB !== undefined && detalle.subtotalCantB !== null) {
                detallePayload.subtotalCantB = Number(detalle.subtotalCantB);
            }
            if (detalle.porcSubtotalCantB !== undefined && detalle.porcSubtotalCantB !== null) {
                detallePayload.porcSubtotalCantB = Number(detalle.porcSubtotalCantB);
            }
            if (detalle.porcCantB !== undefined && detalle.porcCantB !== null) {
                detallePayload.porcCantB = Number(detalle.porcCantB);
            }
            
            // NO incluir subtotal - el backend lo calcula
            
            return detallePayload;
        });
        // Construir payload con estructura plana (como el GET)
        const payload: any = {
            nroFormulario: id, // Puede coincidir con el id de la URL o omitirse
            fechaEmision: fechaEmisionISO,
            fechaVencim: fechaVencimISO,
            nroTipoFormulario: pedidoVenta.nroTipoFormulario || '12',
            tipoCliente: pedidoVenta.tipoCliente || 'C',
            nroCliente: pedidoVenta.nroCliente || 0,
            nombre: pedidoVenta.nombre || '',
            nroVendedor: pedidoVenta.nroVendedor || 0,
            nroLista: pedidoVenta.nroLista || '',
            codigoMoneda: typeof pedidoVenta.codigoMoneda === 'number' 
                ? pedidoVenta.codigoMoneda 
                : (typeof pedidoVenta.codigoMoneda === 'string' && pedidoVenta.codigoMoneda === 'ARS' 
                    ? 0 
                    : Number(pedidoVenta.codigoMoneda) || 0),
            importeTotal: Math.round((pedidoVenta.importeTotal || 0) * 100) / 100, // Redondeado a 2 decimales
            asentado: pedidoVenta.asentado || 'N',
            descripcion: pedidoVenta.descripcion || '',
            usuario: pedidoVenta.usuario || '',
            nroSuc: (pedidoVenta as any).nroSuc || DEFAULT_NRO_SUC,
            detalles: detalles
        };

        // Incluir campos que vienen del pedido original (siempre incluir con valores por defecto)
        payload.letra = (pedidoVenta as any).letra || '';
        payload.nroComprobante = (pedidoVenta as any).nroComprobante ?? 0;
        payload.facturado = pedidoVenta.facturado || 'N';
        payload.nroFormularioFactura = pedidoVenta.nroFormularioFactura ?? 0;
        payload.integrar = pedidoVenta.integrar ?? 0;

        // Agregar campos nuevos (siempre incluir, incluso si son undefined)
        payload.nroUbicacionOrigen = nroUbicacionOrigen ?? 0;
        payload.ordenDeCompra = ((pedidoVenta as any).ordenCompra || (pedidoVenta as any).ordenDeCompra || '').trim();

        // Extraer nroExpreso de transporte (formato: "NOMBRE (número)" o solo el número como string)
        const transporteValue = (pedidoVenta as any).transporte || '';
        let nroExpreso: number = 0;
        if (transporteValue) {
            try {
                // Si viene en formato "NOMBRE (número)", extraer el número
                const match = String(transporteValue).match(/\((\d+)\)/);
                if (match && match[1]) {
                    nroExpreso = Number(match[1]);
                } else {
                    // Si no tiene formato, intentar convertir directamente a número
                    const numValue = Number(transporteValue);
                    if (!isNaN(numValue) && numValue > 0) {
                        nroExpreso = numValue;
                    }
                }
            } catch (error) {
                console.warn('Error al extraer nroExpreso:', error);
            }
        }
        payload.nroExpreso = nroExpreso;

        // Agregar formaDePago (siempre incluir, incluso si está vacío)
        payload.formaDePago = ((pedidoVenta as any).formaDePago || '').trim();

        // Agregar idCompania (solo si tiene valor válido)
        const idCompaniaValue = (pedidoVenta as any).idCompania;
        if (idCompaniaValue !== undefined && idCompaniaValue !== null && String(idCompaniaValue).trim() !== '') {
            payload.idCompania = String(idCompaniaValue).trim();
        }

        // Agregar campos opcionales (siempre incluir, incluso si son 0)
        payload.porcDesc = pedidoVenta.porcDesc !== undefined && pedidoVenta.porcDesc !== null 
            ? Number(pedidoVenta.porcDesc) 
            : 0;
        payload.porcDesc2 = pedidoVenta.porcDesc2 !== undefined && pedidoVenta.porcDesc2 !== null 
            ? Number(pedidoVenta.porcDesc2) 
            : 0;
        payload.porcDesc3 = pedidoVenta.porcDesc3 !== undefined && pedidoVenta.porcDesc3 !== null 
            ? Number(pedidoVenta.porcDesc3) 
            : 0;
        payload.tipoDesc = String((pedidoVenta as any).tipoDesc ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P';
        payload.tipoDesc2 = String((pedidoVenta as any).tipoDesc2 ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P';
        payload.tipoDesc3 = String((pedidoVenta as any).tipoDesc3 ?? 'P').trim().toUpperCase() === 'M' ? 'M' : 'P';
        payload.importeDesc = Number((pedidoVenta as any).importeDesc ?? 0) || 0;
        payload.importeDesc2 = Number((pedidoVenta as any).importeDesc2 ?? 0) || 0;
        payload.importeDesc3 = Number((pedidoVenta as any).importeDesc3 ?? 0) || 0;

        payload.tipoIva = String((pedidoVenta as any).tipoIVA ?? (pedidoVenta as any).tipoIva ?? '').trim() || undefined;
        payload.cuit = String((pedidoVenta as any).cuit ?? '').trim() || undefined;

        console.log('📤 PUT /api/pedidosventa/' + id, payload);
        console.log('📤 Campos incluidos:', {
            nroExpreso: payload.nroExpreso,
            formaDePago: payload.formaDePago,
            porcDesc: payload.porcDesc,
            porcDesc2: payload.porcDesc2,
            porcDesc3: payload.porcDesc3,
            ordenDeCompra: payload.ordenDeCompra,
            nroUbicacionOrigen: payload.nroUbicacionOrigen
        });

        const response = await api.put<PedidoVenta>(`/api/pedidosventa/${id}`, payload);
        return withPedidoNotasTrimmed(response.data);
    },

    deletePedidoVenta: async (id: number): Promise<void> => {
        await api.delete(`/api/pedidosventa/${id}`);
    },

    getPedidoVentaPDF: async (id: number): Promise<Blob> => {
        const response = await api.get(`/api/PedidosVenta/${id}/pdf`, {
            responseType: 'blob'
        });
        return response.data;
    },

    marcarImpreso: async (id: number): Promise<void> => {
        await api.post(`/api/PedidosVenta/${id}/marcar-impreso`);
    },

    marcarListoPicking: async (id: number): Promise<void> => {
        await api.post(`/api/PedidosVenta/${id}/marcar-listo-picking`);
    },

    marcarPreparado: async (id: number): Promise<void> => {
        await api.post(`/api/PedidosVenta/${id}/marcar-preparado`);
    },

    /**
     * POST `/api/PedidosVenta/{id}/conversion-automatica?modo=maestros|porLinea`
     * La pantalla usa `maestros`. La copia al otro tenant va por `copiar-relacionados`.
     */
    conversionAutomatica: async (
        id: number,
        options: ConversionAutomaticaOptions = {}
    ): Promise<void> => {
        const modo = options.modo ?? 'maestros';
        await api.post(`/api/PedidosVenta/${id}/conversion-automatica`, null, {
            params: { modo },
        });
    },

    /**
     * POST `/api/PedidosVenta/{id}/conversion-automatica?modo=maestros`
     * Parte la cantidad según el % del cliente (o del proveedor del artículo).
     * Si el body trae el pedido, se devuelve normalizado.
     */
    conversionDesdeMaestros: async (id: number): Promise<PedidoVenta | null> => {
        const response = await api.post<unknown>(`/api/PedidosVenta/${id}/conversion-automatica`, null, {
            params: { modo: 'maestros' },
        });
        const data = response.data;
        if (!data || typeof data !== 'object') return null;
        const row = data as Record<string, unknown>;
        const detalles = row.detalles ?? row.Detalles;
        if (!Array.isArray(detalles)) return null;
        return normalizePedidoVentaFromApi(data);
    },

    /** Misma acción sobre varios pedidos (secuencial). */
    conversionAutomaticaBulk: async (
        ids: number[],
        options: ConversionAutomaticaOptions = {}
    ): Promise<{ ok: number; error: number }> => {
        const uniqueIds = Array.from(new Set(ids.filter((id) => Number.isFinite(id) && id > 0)));
        if (uniqueIds.length === 0) return { ok: 0, error: 0 };

        const modo = options.modo ?? 'maestros';
        let ok = 0;
        let error = 0;
        for (const id of uniqueIds) {
            try {
                await api.post(`/api/PedidosVenta/${id}/conversion-automatica`, null, {
                    params: { modo },
                });
                ok += 1;
            } catch {
                error += 1;
            }
        }
        return { ok, error };
    },

    conversionDesdeMaestrosBulk: async (ids: number[]): Promise<{ ok: number; error: number }> => {
        const uniqueIds = Array.from(new Set(ids.filter((id) => Number.isFinite(id) && id > 0)));
        if (uniqueIds.length === 0) return { ok: 0, error: 0 };
        let ok = 0;
        let error = 0;
        for (const id of uniqueIds) {
            try {
                await api.post(`/api/PedidosVenta/${id}/conversion-automatica`, null, {
                    params: { modo: 'maestros' },
                });
                ok += 1;
            } catch {
                error += 1;
            }
        }
        return { ok, error };
    },

    anularPedido: async (id: number): Promise<void> => {
        await api.post(`/api/PedidosVenta/${id}/anular`);
    },

    getPedidosEnEspera: async (params: PedidoVentaSearchParams): Promise<PedidoVentaPagedResult> => {
        const queryParams = buildVentaSearchQueryParams(params);
        const response = await api.get<PedidoVentaPagedResult>('/api/PedidosVenta/pes', { params: queryParams });
        return {
            ...response.data,
            items: (response.data.items || []).map((item) => normalizePedidoVentaFromApi(item)),
        };
    },

    /** Arma el pedido en espera con las cantidadesPES ya guardadas en el pedido. */
    generarPedidoEnEspera: async (id: number): Promise<PedidoVenta> => {
        const response = await api.post<PedidoVenta>(`/api/PedidosVenta/${id}/generar-pes`);
        return normalizePedidoVentaFromApi(response.data);
    },

    /** Convierte un pedido en espera en un pedido de venta. */
    generarPedidoDesdeEspera: async (id: number): Promise<PedidoVenta> => {
        const response = await api.post<PedidoVenta>(`/api/PedidosVenta/pes/${id}/generar-pedido`);
        return normalizePedidoVentaFromApi(response.data);
    },

    /** Empresas padre / hija / hermana a las que se puede copiar la porción CantidadB. */
    getTenantsRelacionados: async (): Promise<TenantRelacionadoPedido[]> => {
        const response = await api.get<unknown>('/api/PedidosVenta/tenants-relacionados');
        const data = response.data;
        const lista = Array.isArray(data) ? data : [];
        return lista
            .map(mapTenantRelacionadoPedido)
            .filter((item): item is TenantRelacionadoPedido => item != null);
    },

    /**
     * Copia la porción CantidadB al destino configurado en la sucursal (IniciarEn).
     * Si se informan tenantIds, el tenant de IniciarEn tiene que estar en esa lista.
     */
    copiarRelacionados: async (id: number, tenantIds?: number[]): Promise<CopiarPedidoRelacionadosResult> => {
        const ids = Array.from(new Set((tenantIds ?? []).filter((n) => Number.isFinite(n) && n > 0)));
        const response = await api.post<unknown>(
            `/api/PedidosVenta/${id}/copiar-relacionados`,
            ids.length > 0 ? { tenantIds: ids } : {},
        );
        return mapCopiarPedidoRelacionadosResult<PedidoVenta>(response.data);
    },

    /** Envía el pedido al email del cliente, con el PDF adjunto. */
    enviarPedidoPorEmailAlCliente: async (
        id: number
    ): Promise<{ enviado: boolean; mensaje?: string }> => {
        const response = await api.post<{ enviado: boolean; mensaje?: string }>(
            `/api/PedidosVenta/${id}/enviar-email-cliente`
        );
        return response.data;
    },
};

export default pedidoVentaService; 